import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Play, CheckCircle, Square, UserCheck, GitBranch, Clock, Save } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';

interface NodeData {
  id?: string;
  label: string;
  type: string;
  assignee?: string;
  approver?: string;
  timeout?: string;
  condition?: string;
  description?: string;
}

interface NodeEditModalProps {
  node: NodeData | null;
  onSave: (node: NodeData) => void;
  onClose: () => void;
}

const NodeEditModal = ({ node, onSave, onClose }: NodeEditModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<NodeData>({
    label: node?.label || '',
    type: node?.type || 'task',
    assignee: node?.assignee || '',
    approver: node?.approver || '',
    timeout: node?.timeout || '',
    condition: node?.condition || '',
    description: node?.description || ''
  });

  const handleSave = () => {
    if (!formData?.label?.trim()) {
      alert(t('Please enter a node label'));
      return;
    }
    onSave({ ...node, ...formData });
  };

  const nodeTypes = [
    { value: 'start', label: t('Start'), icon: 'Play' },
    { value: 'end', label: t('End'), icon: 'CheckCircle' },
    { value: 'task', label: t('Task'), icon: 'Square' },
    { value: 'approval', label: t('Approval'), icon: 'UserCheck' },
    { value: 'decision', label: t('Decision'), icon: 'GitBranch' },
    { value: 'delay', label: t('Delay'), icon: 'Clock' }
  ];

  const assigneeOptions = [
    { value: 'HR Team', label: t('HR Team') },
    { value: 'IT Team', label: t('IT Team') },
    { value: 'Finance Team', label: t('Finance Team') },
    { value: 'Direct Manager', label: t('Direct Manager') },
    { value: 'Department Head', label: t('Department Head') },
    { value: 'HR Coordinator', label: t('HR Coordinator') },
    { value: 'Payroll Team', label: t('Payroll Team') },
    { value: 'Project Manager', label: t('Project Manager') }
  ];

  const approverOptions = [
    { value: 'Direct Manager', label: t('Direct Manager') },
    { value: 'Department Head', label: t('Department Head') },
    { value: 'HR Manager', label: t('HR Manager') },
    { value: 'HR Director', label: t('HR Director') },
    { value: 'Finance Manager', label: t('Finance Manager') },
    { value: 'CEO', label: t('CEO/Executive') }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('Edit Node')}</h2>
            <p className="text-sm text-gray-600">{t('Configure node properties and behavior')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-4">
          {/* Node Type */}
          <Select
            label={t('Node Type')}
            required
            value={formData?.type}
            onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
            options={nodeTypes}
          />

          {/* Node Label */}
          <Input
            label={t('Node Label')}
            required
            value={formData?.label}
            onChange={(e) => setFormData({ ...formData, label: e?.target?.value })}
            placeholder={t('e.g., Send Welcome Email')}
          />

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              {t('Description')}
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder={t('Describe what this node does...')}
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
            />
          </div>

          {/* Task-specific fields */}
          {formData?.type === 'task' && (
            <Select
              label={t('Assignee')}
              value={formData?.assignee}
              onChange={(e) => setFormData({ ...formData, assignee: e?.target?.value })}
              options={assigneeOptions}
              placeholder={t('Select assignee')}
            />
          )}

          {/* Approval-specific fields */}
          {formData?.type === 'approval' && (
            <>
              <Select
                label={t('Approver')}
                required
                value={formData?.approver}
                onChange={(e) => setFormData({ ...formData, approver: e?.target?.value })}
                options={approverOptions}
                placeholder={t('Select approver')}
              />
              <Input
                label={t('Timeout (hours)')}
                type="number"
                value={formData?.timeout}
                onChange={(e) => setFormData({ ...formData, timeout: e?.target?.value })}
                placeholder={t('e.g., 24')}
                description={t('Time limit for approval before escalation')}
              />
            </>
          )}

          {/* Decision-specific fields */}
          {formData?.type === 'decision' && (
            <Input
              label={t('Condition')}
              value={formData?.condition}
              onChange={(e) => setFormData({ ...formData, condition: e?.target?.value })}
              placeholder={t('e.g., duration > 5 days')}
              description={t('Condition for routing decision')}
            />
          )}

          {/* Delay-specific fields */}
          {formData?.type === 'delay' && (
            <Input
              label={t('Delay Duration')}
              value={formData?.timeout}
              onChange={(e) => setFormData({ ...formData, timeout: e?.target?.value })}
              placeholder={t('e.g., 2 hours, 1 day')}
              description={t('How long to wait before proceeding')}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="default"
            iconName="Save"
            onClick={handleSave}
            disabled={!formData?.label?.trim()}
          >
            {t('Save Changes')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NodeEditModal;
